import type { TourStep } from '#nuxt-tour/props';
import { breakpointsTailwind, useBreakpoints, useStorage } from '@vueuse/core';

export const useUiStore = defineStore('ui-store', () => {
  const { $i18n } = useNuxtApp();
  const { t } = $i18n;
  const route = useRoute();
  const toolbarStore = useToolbarStore();
  const runtimeConfig = useRuntimeConfig();
  const breakpoints = useBreakpoints(breakpointsTailwind);
  const mdAndSmaller = breakpoints.smallerOrEqual('md');

  let lastBreakpointValue = mdAndSmaller.value;
  let breakpointChanged = false;

  const localConversionMode = useStorage('localConversionMode', false);
  const oneDriveMode = useStorage('oneDriveMode', false);
  const debugMode = ref(false);
  const potreeDebugPanelVisible = ref(true);
  const language = useStorage('language', 'en');
  const fpsVisible = useStorage('fpsVisible', false);

  const fastNavPreset = useStorage('fastNavPreset', 'low');
  const fastNavOptions = useStorage('fastNavOptions', fastNavPresets.low);
  const ifcTypesList = useStorage('ifcTypesList', ifcTypesListInit);

  const quickTourRef = ref(null);
  const quickTourDialogVisible = ref(true);
  const quickTourLocalStorageItem = localStorage.getItem('nt-default');

  if (quickTourLocalStorageItem) {
    quickTourDialogVisible.value = quickTourLocalStorageItem !== 'true';
  }

  const skipQuickTour = runtimeConfig.public.MODE_CICD === 1;
  if (skipQuickTour) {
    quickTourDialogVisible.value = false;
  }

  const settingsDialogVisible = ref(false);
  const uploaderDialogVisible = ref(false);
  const annotationDialogVisible = ref(false);
  const hotkeysDialogVisible = ref(false);
  const userDialogVisible = ref(false);
  const projectPasswordDialogVisible = ref(false);
  const uploaderAutoCloseAllowed = ref(false);

  const menuPanelVisible = ref(false);
  const explorerPanelVisible = ref(false);
  const propertiesPanelVisible = ref(false);
  const savedViewsPanelVisible = ref(false);
  const filtersPanelVisible = ref(false);
  const groupingPanelVisible = ref(false);
  const troubleshootDialogVisible = ref(false);
  const troubleshootDialogSentryErrorId = ref('');

  const explorerPanelTab = ref('models');
  const explorerGraphMode = ref(false);

  const userViewTab = ref<'projects' | 'account' | 'access' | 'subscription'>('projects');

  const quickTourSteps: TourStep[] = [
    {
      target: '[data-locator="toggle-explorer"]',
      body: `<strong>${t('views.dialog.quicktour.step1.title')}</strong><p>${t('views.dialog.quicktour.step1.description')}</p>`,
      popperConfig: {
        placement: 'bottom-start',
      },
    },
    {
      target: '[data-locator="toggle-select"]',
      body: `<strong>${t('views.dialog.quicktour.step2.title')}</strong><p>${t('views.dialog.quicktour.step2.description')}</p>`,
      popperConfig: {
        placement: 'bottom-start',
      },
    },
    {
      target: '[data-locator="toggle-slice"]',
      body: `<strong>${t('views.dialog.quicktour.step3.title')}</strong><p>${t('views.dialog.quicktour.step3.description')}</p>`,
      popperConfig: {
        placement: 'bottom-start',
      },
    },
    {
      target: '[data-locator="share-dialog-button"]',
      body: `<strong>${t('views.dialog.quicktour.step4.title')}</strong><p>${t('views.dialog.quicktour.step4.description')}</p>`,
      popperConfig: {
        placement: 'bottom-end',
      },
    },
    {
      target: '#xeokit-viewer',
      body: `<strong>${t('views.dialog.quicktour.step5.title')}</strong><p>${t('views.dialog.quicktour.step5.description')}</p>`,
      popperConfig: {
        placement: 'bottom-start',
      },
    },
  ];

  onMounted(() => {
    if (route.query.debug === 'true') {
      debugMode.value = true;
    }

    if (route.query.conversion === 'local') {
      localConversionMode.value = true;
    }

    if (route.query.conversion === 'server') {
      localConversionMode.value = false;
    }

    if (route.query.onedrive === 'true') {
      oneDriveMode.value = true;
    }

    if (route.query.onedrive === 'false') {
      oneDriveMode.value = false;
    }

    window.addEventListener('resize', onResize);
  });

  function onResize() {
    if (lastBreakpointValue !== mdAndSmaller.value) {
      lastBreakpointValue = mdAndSmaller.value;
      breakpointChanged = true;
    }

    if (breakpointChanged) {
      menuPanelVisible.value = false;
      explorerPanelVisible.value = false;
      propertiesPanelVisible.value = false;
      savedViewsPanelVisible.value = false;
      filtersPanelVisible.value = false;
      groupingPanelVisible.value = false;
      troubleshootDialogVisible.value = false;
      toolbarStore.minimapActive = false;
      toolbarStore.annotationsMode.active = false;
      toolbarStore.sliceObjectsMode.active = false;

      breakpointChanged = false;
    }
  }

  watch(explorerPanelVisible, (state) => {
    if (state && mdAndSmaller.value) {
      menuPanelVisible.value = false;
      propertiesPanelVisible.value = false;
      savedViewsPanelVisible.value = false;
      filtersPanelVisible.value = false;
      groupingPanelVisible.value = false;
      toolbarStore.minimapActive = false;
      toolbarStore.annotationsMode.active = false;
    }
  });

  watch(menuPanelVisible, (state) => {
    if (state && mdAndSmaller.value) {
      explorerPanelVisible.value = false;
      propertiesPanelVisible.value = false;
      savedViewsPanelVisible.value = false;
      filtersPanelVisible.value = false;
      groupingPanelVisible.value = false;
      toolbarStore.minimapActive = false;
      toolbarStore.annotationsMode.active = false;
    }
  });

  watch(propertiesPanelVisible, (state) => {
    if (state && mdAndSmaller.value) {
      explorerPanelVisible.value = false;
      menuPanelVisible.value = false;
      savedViewsPanelVisible.value = false;
      filtersPanelVisible.value = false;
      groupingPanelVisible.value = false;
      toolbarStore.minimapActive = false;
      toolbarStore.annotationsMode.active = false;
    }
  });

  watch(savedViewsPanelVisible, (state) => {
    if (state && mdAndSmaller.value) {
      explorerPanelVisible.value = false;
      menuPanelVisible.value = false;
      propertiesPanelVisible.value = false;
      filtersPanelVisible.value = false;
      groupingPanelVisible.value = false;
      toolbarStore.minimapActive = false;
      toolbarStore.annotationsMode.active = false;
    }
  });

  watch(filtersPanelVisible, (state) => {
    if (state && mdAndSmaller.value) {
      explorerPanelVisible.value = false;
      menuPanelVisible.value = false;
      propertiesPanelVisible.value = false;
      savedViewsPanelVisible.value = false;
      groupingPanelVisible.value = false;
      toolbarStore.minimapActive = false;
      toolbarStore.annotationsMode.active = false;
    }
  });

  watch(groupingPanelVisible, (state) => {
    if (state && mdAndSmaller.value) {
      explorerPanelVisible.value = false;
      menuPanelVisible.value = false;
      propertiesPanelVisible.value = false;
      savedViewsPanelVisible.value = false;
      filtersPanelVisible.value = false;
      toolbarStore.minimapActive = false;
      toolbarStore.annotationsMode.active = false;
    }
  });

  watch(
    () => toolbarStore.minimapActive,
    (state) => {
      if (state && mdAndSmaller.value) {
        explorerPanelVisible.value = false;
        menuPanelVisible.value = false;
        propertiesPanelVisible.value = false;
        savedViewsPanelVisible.value = false;
        filtersPanelVisible.value = false;
        groupingPanelVisible.value = false;
        toolbarStore.annotationsMode.active = false;
      }
    },
  );

  watch(
    () => toolbarStore.annotationsMode.active,
    (state) => {
      if (state && mdAndSmaller.value) {
        explorerPanelVisible.value = false;
        menuPanelVisible.value = false;
        propertiesPanelVisible.value = false;
        savedViewsPanelVisible.value = false;
        filtersPanelVisible.value = false;
        groupingPanelVisible.value = false;
        toolbarStore.minimapActive = false;
      }
    },
  );

  window.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') {
      explorerPanelVisible.value = false;
      menuPanelVisible.value = false;
      propertiesPanelVisible.value = false;
      savedViewsPanelVisible.value = false;
      filtersPanelVisible.value = false;
      groupingPanelVisible.value = false;
      toolbarStore.minimapActive = false;

      toolbarStore.setHideObjects(false);
      toolbarStore.setMeasureDistance(false);
      toolbarStore.setSliceObjects(false);
      toolbarStore.setAnnotationsMode(false);

      toolbarStore.setSelectObjects(true);
    }
  });

  function resetQuickTour() {
    settingsDialogVisible.value = false;

    try {
      // @ts-ignore
      quickTourRef.value?.resetTour();
    } catch (err) {
      console.error('Error resetting quick tour:', err);
    }

    //localStorage.setItem('nt-default', 'false');
    //quickTourDialogVisible.value = true;
  }

  return {
    language,
    localConversionMode,
    oneDriveMode,
    debugMode,
    potreeDebugPanelVisible,
    fpsVisible,
    fastNavPreset,
    fastNavOptions,
    ifcTypesList,
    quickTourDialogVisible,
    quickTourRef,
    quickTourSteps,
    resetQuickTour,
    settingsDialogVisible,
    uploaderDialogVisible,
    annotationDialogVisible,
    hotkeysDialogVisible,
    userDialogVisible,
    projectPasswordDialogVisible,
    uploaderAutoCloseAllowed,
    menuPanelVisible,
    explorerPanelVisible,
    propertiesPanelVisible,
    savedViewsPanelVisible,
    filtersPanelVisible,
    groupingPanelVisible,
    explorerPanelTab,
    explorerGraphMode,
    troubleshootDialogVisible,
    troubleshootDialogSentryErrorId,
    userViewTab,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUiStore, import.meta.hot));
}
